<template>
  <div
    ref="draggableContainer"
    class="draggable-container"
    :style="{ transform: `translate(${position.x}px, ${position.y}px)` }"
    @mousedown="startDrag"
    @touchstart="startDrag"
  >
    <slot :is-dragging="isDragging" />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onUnmounted } from 'vue'

interface Props {
  /** Unique identifier for localStorage key */
  storageKey?: string
  /** Initial position if no saved position exists */
  defaultPosition?: { x: number; y: number }
  /** CSS selector for elements that should not trigger drag */
  excludeSelectors?: string
}

const props = withDefaults(defineProps<Props>(), {
  storageKey: 'draggable-position',
  defaultPosition: () => ({ x: 0, y: 0 }),
  excludeSelectors: 'button, a, input, select, textarea'
})

// Dragging state
const isDragging = ref(false)
const draggableContainer = ref<HTMLElement | null>(null)

// Position tracking
const position = reactive({ x: 0, y: 0 })
const dragStart = reactive({ x: 0, y: 0 })
const elementStart = reactive({ x: 0, y: 0 })

// Load saved position from localStorage
onMounted(() => {
  const savedPosition = localStorage.getItem(props.storageKey)
  if (savedPosition) {
    const parsed = JSON.parse(savedPosition)
    position.x = parsed.x
    position.y = parsed.y
  } else {
    position.x = props.defaultPosition.x
    position.y = props.defaultPosition.y
  }
})

// Save position to localStorage
const savePosition = () => {
  localStorage.setItem(props.storageKey, JSON.stringify({ x: position.x, y: position.y }))
}

// Drag functionality
const startDrag = (e: MouseEvent | TouchEvent) => {
  // Don't start drag if clicking on excluded elements
  const target = e.target as HTMLElement
  if (props.excludeSelectors && target.closest(props.excludeSelectors)) {
    return
  }

  isDragging.value = true

  const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
  const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY

  dragStart.x = clientX
  dragStart.y = clientY
  elementStart.x = position.x
  elementStart.y = position.y

  document.addEventListener('mousemove', drag)
  document.addEventListener('mouseup', stopDrag)
  document.addEventListener('touchmove', drag, { passive: false })
  document.addEventListener('touchend', stopDrag)

  // Prevent text selection during drag
  e.preventDefault()
}

const drag = (e: MouseEvent | TouchEvent) => {
  if (!isDragging.value) return

  const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
  const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY

  const deltaX = clientX - dragStart.x
  const deltaY = clientY - dragStart.y

  // Calculate new position with boundaries
  const newX = elementStart.x + deltaX
  const newY = elementStart.y + deltaY

  // Get viewport dimensions and element dimensions
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const elementRect = draggableContainer.value?.getBoundingClientRect()

  if (elementRect) {
    const elementWidth = elementRect.width
    const elementHeight = elementRect.height

    // Apply boundaries (keep element within viewport)
    const maxX = viewportWidth - elementWidth - 20 // 20px margin from edge
    const minX = -viewportWidth + elementWidth + 20
    const maxY = viewportHeight - elementHeight - 20
    const minY = -viewportHeight + elementHeight + 20

    position.x = Math.max(minX, Math.min(maxX, newX))
    position.y = Math.max(minY, Math.min(maxY, newY))
  }

  // Prevent scrolling on touch devices
  if ('touches' in e) {
    e.preventDefault()
  }
}

const stopDrag = () => {
  if (isDragging.value) {
    isDragging.value = false
    savePosition()
  }

  document.removeEventListener('mousemove', drag)
  document.removeEventListener('mouseup', stopDrag)
  document.removeEventListener('touchmove', drag)
  document.removeEventListener('touchend', stopDrag)
}

// Cleanup event listeners on unmount
onUnmounted(() => {
  stopDrag()
})
</script>

<style scoped>
.draggable-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  cursor: move;
  transition: transform 0.1s ease-out;
  user-select: none;
}

.draggable-container:active {
  cursor: grabbing;
}

/* Prevent text selection during drag */
.draggable-container * {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* Mobile touch improvements */
@media (max-width: 768px) {
  .draggable-container {
    bottom: 10px;
    right: 10px;
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  .draggable-container {
    transition: none;
  }
}
</style>
